import type { User } from "firebase/auth";

export interface PlatformUserRecord {
  uid: string;
  email: string | null;
  displayName: string | null;
  disabled: boolean;
  emailVerified: boolean;
  createdAt: string | null;
  lastSignInAt: string | null;
  platformRoles: string[];
  platformPanelAccess: boolean;
  role: string | null;
}

const getBackendBaseUrl = (): string => {
  const raw = process.env.REACT_APP_BACKEND_URL;
  if (!raw) {
    throw new Error("Missing REACT_APP_BACKEND_URL environment variable.");
  }
  return raw.replace(/\/+$/, "");
};

const parseErrorDetail = (
  data: unknown,
  status: number,
): string => {
  if (
    data &&
    typeof data === "object" &&
    "detail" in data &&
    typeof (data as { detail?: unknown }).detail === "string"
  ) {
    return (data as { detail: string }).detail;
  }
  return `Backend error (${status}).`;
};

export const listPlatformUsers = async (
  user: User,
): Promise<PlatformUserRecord[]> => {
  const token = await user.getIdToken();
  const response = await fetch(`${getBackendBaseUrl()}/api/v1/platform/users`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = (await response.json().catch(() => null)) as unknown;
  if (!response.ok) {
    throw new Error(parseErrorDetail(data, response.status));
  }
  return Array.isArray(data) ? (data as PlatformUserRecord[]) : [];
};

export interface CreatePlatformUserInput {
  email: string;
  password?: string;
  displayName?: string;
  disabled?: boolean;
}

export const createPlatformUser = async (
  user: User,
  input: CreatePlatformUserInput,
): Promise<PlatformUserRecord> => {
  const token = await user.getIdToken();
  const response = await fetch(`${getBackendBaseUrl()}/api/v1/platform/users`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email: input.email.trim(),
      password: input.password?.trim() || undefined,
      displayName: input.displayName?.trim() || undefined,
      disabled: input.disabled ?? false,
    }),
  });
  const data = (await response.json().catch(() => null)) as unknown;
  if (!response.ok) {
    throw new Error(parseErrorDetail(data, response.status));
  }
  return data as PlatformUserRecord;
};

export const revokePlatformUser = async (
  user: User,
  uid: string,
): Promise<void> => {
  const token = await user.getIdToken();
  const response = await fetch(
    `${getBackendBaseUrl()}/api/v1/platform/users/${encodeURIComponent(uid)}`,
    {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  if (!response.ok) {
    const data = (await response.json().catch(() => null)) as unknown;
    throw new Error(parseErrorDetail(data, response.status));
  }
};
